import type { ConfigApi } from '@backstage/core-plugin-api';
import { okdConsoleUrl } from './okdClusters';

// Single source of truth for portal page shortcuts used in search and sidebar.
export type PortalPage = {
  label: string;
  section: string;
  to: string;
  external: boolean;
};

const OKD_CONSOLE_PAGES: PortalPage[] = [
  { id: 'us-east-1', label: 'OKD us-east-1' },
  { id: 'eu-central-1', label: 'OKD eu-central-1' },
  { id: 'us-east-2', label: 'OKD us-east-2' },
  { id: 'us-west-2', label: 'OKD us-west-2' },
  { id: 'ca-central-1', label: 'OKD ca-central-1' },
  { id: 'eu-west-2', label: 'OKD eu-west-2' },
  { id: 'ap-southeast-1', label: 'OKD ap-southeast-1' },
  { id: 'ap-southeast-2', label: 'OKD ap-southeast-2' },
  { id: 'af-south-1', label: 'OKD af-south-1' },
  { id: 'eus-azure', label: 'OKD eastus Azure' },
  { id: 'gwc-azure', label: 'OKD Germany West Central Azure' },
  { id: 'beta-us-east-2', label: 'OKD Beta us-east-2' },
  { id: 'mgmt-1', label: 'OKD mgmt-1' },
].map(({ id, label }) => ({ label, section: 'OKD Consoles', to: okdConsoleUrl(id), external: true }));

const STATIC_PAGES: PortalPage[] = [
  { label: 'Software Catalog', section: 'Catalog', to: '/catalog', external: false },
  { label: 'Operators Inventory', section: 'Catalog', to: '/operators', external: false },
  { label: 'Infrastructure Graph', section: 'Catalog', to: '/infrastructure-graph', external: false },
  { label: 'AI Marketplace', section: 'Miscellaneous', to: '/ai-marketplace', external: false },
  { label: 'Hydra Docs', section: 'Documentation', to: '/docs/default/component/hydra-docs', external: false },
  { label: 'API Guidelines', section: 'Documentation', to: '/docs/default/component/api-guidelines', external: false },
  { label: 'GraviteeAPI Operator Docs', section: 'Documentation', to: '/docs/default/component/hydra-docs/developer-tools/graviteeapi-operator/', external: false },
  { label: 'Docs MCP', section: 'Documentation', to: '/docs/default/component/mcp-server', external: false },
  { label: 'Vault', section: 'Tools', to: 'https://vault.hydra.verint.com/', external: true },
  { label: 'Quay', section: 'Tools', to: 'https://quay.hydra.verint.com/', external: true },
  { label: 'Harness', section: 'Tools', to: 'https://app.harness.io/auth/#/signin', external: true },
  { label: 'Harness CI/CD', section: 'Tools', to: '/harness', external: false },
  { label: 'Quay Registry', section: 'Tools', to: '/quay', external: false },
  { label: 'API Management', section: 'Tools', to: 'https://console.api.verint.com/', external: true },
  { label: 'DataDog', section: 'Tools', to: '/datadog', external: false },
  { label: 'Datadog APIs Monitor', section: 'Tools', to: '/datadog', external: false },
  ...OKD_CONSOLE_PAGES,
];

/** Returns the full portal page list, including config-driven entries (Teams channels, Kubecost). */
export function buildPortalPages(configApi: ConfigApi): PortalPage[] {
  const announcements = configApi.getOptionalString('teams.channels.announcements') ?? '';
  const general = configApi.getOptionalString('teams.channels.general') ?? '';
  const graviteeApim = configApi.getOptionalString('teams.channels.graviteeApim') ?? '';

  const dynamic: PortalPage[] = [
    ...(announcements ? [{ label: 'Hydra Announcements', section: 'Documentation', to: announcements, external: true }] : []),
    ...(general ? [{ label: 'Hydra General', section: 'Documentation', to: general, external: true }] : []),
    ...(graviteeApim ? [{ label: 'Gravitee APIM Channel', section: 'Documentation', to: graviteeApim, external: true }] : []),
  ];

  return [...STATIC_PAGES, ...dynamic];
}
